import type { FormEvent, JSX } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'

import {
  assignCustomerToCart,
  customerAccountUrlFragmentRE,
  loadCart,
  loginCustomer,
  logoutCustomer,
  signupCustomer,
} from '../../../../store/actions'
import { generateCustomerAccountUrl, generateCustomerOrdersUrl, generateShopUrl } from '../../../../urlGenerators'
import { getPlain } from '../../../../store/utils'
import { track } from '../../../../utils/tracking'
import { useTranslate, withComponents } from '../../../../utils/translate'
import { withQueryAndScope } from '../../../../urlGeneratorsPlain'
import Dropdown from '../Dropdown'
import Lightbox from '../../../templateComponents/Lightbox'
import Link from '../../../templateComponents/Link'
import PasswordRevealInput from '../PasswordRevealInput'
import Portal from '../../../templateComponents/Portal'
import useAutoFocus from '../../../../utils/hooks/useAutoFocus'

const customerAccountPath = generateCustomerAccountUrl()
const customerOrdersPath = generateCustomerOrdersUrl()

export default function AccountMenu(): JSX.Element {
  const dispatch = useDispatch<GlobalDispatch>()

  const t = useTranslate('shop', 'components.accountMenuComponent')

  const isLoggedIn = useSelector<State, boolean>((state) => state.getIn(['customer', 'loggedIn'], false))

  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  const [showForm, setShowForm] = useState<null | 'login' | 'signup'>(null)
  const hideForm = () => setShowForm(null)

  // enable deep linking to the login form
  useEffect(() => {
    if (searchParams.has('login')) setShowForm('login')
    if (new URL(window.location.href).hash.includes('showLoginForm')) setShowForm('login')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleLogout() {
    try {
      await dispatch(logoutCustomer())
      if (customerAccountUrlFragmentRE.test(location.pathname)) {
        navigate(withQueryAndScope(generateShopUrl(), location))
      }
    } catch {
      // global API call error handler takes care
    }
  }

  return (
    <div className="header-account-login">
      <Dropdown
        ariaId="account-dropdown"
        ariaLabel={t('accountIcon.accessibilityLabel')}
        buttonLabel={<span className="header-account-icon" />}
      >
        {(close: () => void) =>
          isLoggedIn ? (
            <>
              <li>
                <Link to={customerAccountPath} onClick={close} activeClassName={'popover-item-active'}>
                  {t('accountLink.label')}
                </Link>
              </li>
              <li>
                <Link to={customerOrdersPath} onClick={close} activeClassName={'popover-item-active'}>
                  {t('ordersLink.label')}
                </Link>
              </li>
              <hr></hr>
              <li>
                <button onClick={handleLogout} className="logout-button">
                  {t('logoutButton.label')}
                </button>
              </li>
            </>
          ) : (
            <>
              <li>
                <button onClick={() => setShowForm('login')}>{t('loginButton.label')}</button>
              </li>
              <li>
                <button onClick={() => setShowForm('signup')}>{t('signupButton.label')}</button>
              </li>
            </>
          )
        }
      </Dropdown>
      <Portal>
        <Lightbox show={Boolean(showForm)} onClose={hideForm} lightboxClassName="lightbox-modal">
          {showForm === 'login' && <LoginForm onClose={hideForm} onGoToSignup={() => setShowForm('signup')} />}
          {showForm === 'signup' && <SignupForm onClose={hideForm} onGoToLogin={() => setShowForm('login')} />}
        </Lightbox>
      </Portal>
    </div>
  )
}

type LegalPage = {
  slug: string
  href: string
  title: string
}

type LegalPageLinkProps = {
  slug: string
  newTab?: boolean
}

export function LegalPageLink({ slug: chosenSlug, newTab }: Readonly<LegalPageLinkProps>): JSX.Element {
  const legalPages = getPlain(useSelector<State, LegalPage[]>((state) => state.get('legalPages')))
  const location = useLocation()
  const t = useTranslate('shop')

  const { href, title } = legalPages.find(({ slug }) => slug === chosenSlug) as LegalPage

  return (
    <Link href={withQueryAndScope(href, location) as string} {...(newTab ? { target: '_blank' } : {})}>
      {t(title)}
    </Link>
  )
}

type LoginFormProps = {
  onClose: () => void
  onGoToSignup: () => void
}

export const toggleLabelInvalidClass =
  (invalid: boolean) =>
  (eventOrElement: FormEvent<HTMLFormElement> | HTMLFormElement): void => {
    const element = (eventOrElement?.target || eventOrElement) as HTMLInputElement
    element?.closest('label')?.classList[invalid ? 'add' : 'remove']('form-element-invalid')
  }

function LoginForm({ onClose, onGoToSignup }: Readonly<LoginFormProps>): JSX.Element {
  const cart = getPlain(useSelector<State, Core.Cart | null>((state) => state.get('cart', null)))

  const { confirmedNewEmail } =
    getPlain(useSelector<State, Record<string, any>>((state) => state.get('customer'))) || {}

  const location = useLocation()
  const dispatch = useDispatch()

  const t = useTranslate('shop', 'components.accountMenuComponent.loginModal')

  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const form = useRef<HTMLFormElement>(null)

  const navigate = useNavigate()

  useAutoFocus(form)

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)

    const email = event.target.elements.email.value
    const password = event.target.elements.password.value

    try {
      setError(null)
      await dispatch(loginCustomer({ email, password }, { showErrorNotification: false }))

      if (cart?.productLineItems?.length) await dispatch(assignCustomerToCart())
      else await dispatch(loadCart())

      setSubmitting(false)
      onClose()

      track('customer:signIn', { customer: { email } })

      const redirectTarget = new URL(window.location.href).searchParams.get('redirect') || ''
      const destination = decodeURIComponent(redirectTarget) || customerAccountPath

      navigate(withQueryAndScope(destination, location))
    } catch (error) {
      // scroll up to the error message
      form.current?.scrollIntoView(true)

      if (['unauthorized', 'invalid_grant'].includes(error.message)) {
        setError(t('errors.credentialsInvalid'))
        setSubmitting(false)

        // UX concept wants inputs marked as invalid upon wrong credentials
        toggleLabelInvalidClass(true)(form.current?.email)
        toggleLabelInvalidClass(true)(form.current?.password)
      } else {
        setError(t('errors.genericServerError'))
      }
    }
  }

  return (
    <form
      ref={form}
      className="lightbox-modal-form account-login-form"
      onInvalid={toggleLabelInvalidClass(true)}
      onInput={toggleLabelInvalidClass(false)}
      name="customerLogin"
      onSubmit={handleSubmit}
      autoComplete="on"
    >
      <div className="inner-content">
        <h2 className="title">{t('title')}</h2>
        <p className="description">{t('explanation')}</p>
        {error && <p className="error-message">{error}</p>}
        {confirmedNewEmail && <p className="warning-message">{t('notifications.confirmedNewEmail')}</p>}
        <label>
          {t('emailInputField.label')}*
          <input name="email" autoComplete="email" defaultValue={confirmedNewEmail} type="email" required />
        </label>
        <label>
          {t('passwordInputField.label')}*
          <PasswordRevealInput name="password" autoComplete="password" required />
        </label>
        <Link className="forgot-password-link" to="/forgot-password">
          {t('resetPasswordLink.label')}
        </Link>
      </div>
      <div className="button-wrapper">
        <button className="button button-primary button-pending-state pending" type="submit" disabled={submitting}>
          {t('continueButton.label')}
        </button>
        <button className="button button-outline" type="button" onClick={() => onGoToSignup()}>
          {t('signupButton.label')}
        </button>
      </div>
      <aside>
        {withComponents(t)('legalPageLinks', {
          tac: <LegalPageLink newTab slug="tac" />,
          privacy: <LegalPageLink newTab slug="privacy" />,
        })}
      </aside>
    </form>
  )
}

type SignupFormProps = {
  onClose: () => void
  onGoToLogin: () => void
}

function SignupForm({ onClose, onGoToLogin }: Readonly<SignupFormProps>): JSX.Element {
  const location = useLocation()
  const cart = getPlain(useSelector<State, Core.Cart | null>((state) => state.get('cart', null)))

  const dispatch = useDispatch()

  const t = useTranslate('shop', 'components.accountMenuComponent.signupModal')

  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const form = useRef<HTMLFormElement>(null)

  const navigate = useNavigate()

  useAutoFocus(form)

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)

    const firstName = event.target.elements.firstName.value
    const lastName = event.target.elements.lastName.value
    const email = event.target.elements.email.value
    const initialPassword = event.target.elements.initialPassword.value

    try {
      setError(null)
      await dispatch(signupCustomer({ firstName, lastName, email, initialPassword }, { showErrorNotification: false }))
      if (cart?.productLineItems?.length) await dispatch(assignCustomerToCart())

      setSubmitting(false)
      onClose()

      track('customer:signUp', { customer: { email, firstName, lastName } })

      navigate(withQueryAndScope(customerAccountPath, location))
    } catch (error) {
      // scroll up to the error message
      form.current?.scrollIntoView(true)

      if (/validation failed/.test(error.message)) {
        setError(t('errors.emailAlreadyRegistered'))
      } else {
        setError(t('errors.genericServerError'))
      }
      setSubmitting(false)
    }
  }

  return (
    <form
      ref={form}
      className="lightbox-modal-form account-signup-form"
      onInvalid={toggleLabelInvalidClass(true)}
      onInput={toggleLabelInvalidClass(false)}
      name="customerSignup"
      onSubmit={handleSubmit}
      autoComplete="on"
    >
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a href="#" onClick={() => onGoToLogin()} className="nav-link">
        {t('backToLogin')}
      </a>
      <div className="inner-content">
        <h2 className="title">{t('title')}</h2>
        {error && <p className="error-message">{error}</p>}
        <label>
          {t('firstNameInputField.label')}*
          <input name="firstName" autoComplete="given-name" type="text" required />
        </label>
        <label>
          {t('lastNameInputField.label')}*
          <input name="lastName" autoComplete="family-name" type="text" required />
        </label>
        <label>
          {t('emailInputField.label')}*
          <input name="email" autoComplete="email" type="email" required />
        </label>
        <label>
          {t('passwordInputField.label')}*
          <PasswordRevealInput
            name="initialPassword"
            autoComplete="new-password"
            placeholder={t('passwordInputField.hintText')}
            minLength={8}
            required
          />
        </label>
        <label className="checkbox-wrapper">
          <input type="checkbox" required />
          <div>
            {withComponents(t)('conditionsAcceptedCheckbox.label', {
              tac: <LegalPageLink newTab slug="tac" />,
              privacy: <LegalPageLink newTab slug="privacy" />,
            })}{' '}
            *
          </div>
        </label>
      </div>
      <div className="button-wrapper">
        <button className="button button-primary button-pending-state pending" type="submit" disabled={submitting}>
          {t('createAccountButton.label')}
        </button>
      </div>
    </form>
  )
}
